import { createTenantAdminSchema, updateTenantSchema, type PasswordLink, type TenantDetail } from '@smarton-pbx/shared';
import { Activity, Ban, ChevronLeft, ExternalLink, Play, ShieldCheck, UserPlus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../api';
import { BrandingForm } from '../components/BrandingForm';
import { CopyLink, Dialog, EmptyState, Field, Loading, Notice, PageHeader, Pill } from '../components/ui';
import { TimeZoneSelect } from '../components/pickers';
import { locale, t, tp } from '../i18n';
import { useLoad, useSubmit } from '../lib/hooks';

function formatMinutes(seconds: number): string {
  return `${Math.round(seconds / 60).toLocaleString(locale)} min`;
}

function formatBytes(bytes: string): string {
  const n = Number(bytes);
  const oneDecimal = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
  return n >= 1e9 ? `${(n / 1e9).toLocaleString(locale, oneDecimal)} GB` : `${(n / 1e6).toLocaleString(locale, oneDecimal)} MB`;
}

const actionNames: Record<string, string> = {
  'tenant.created': t('Created the tenant'),
  'tenant.updated': t('Changed settings or branding'),
  'tenant.suspend': t('Suspended the tenant'),
  'tenant.activate': t('Reactivated the tenant'),
  'tenant.admin-created': t('Added an administrator'),
  'tenant.image-uploaded': t('Uploaded an image'),
};

export function TenantDetailPage() {
  const id = useParams().id!;
  const tenant = useLoad(() => api.platform.tenant(id), [id]);
  const usage = useLoad(() => api.platform.tenantUsage(id), [id]);
  const audit = useLoad(() => api.platform.tenantAudit(id), [id]);
  const [addingAdmin, setAddingAdmin] = useState(false);

  async function update(change: Promise<TenantDetail>) {
    tenant.setData(await change);
    audit.reload();
  }

  if (tenant.error) return <Notice kind="error">{tenant.error}</Notice>;
  if (!tenant.data) return <Loading />;
  const detail = tenant.data;

  return (
    <div className="stack-lg">
      <div className="stack" style={{ gap: '0.75rem' }}>
        <Link to="/" className="row muted" style={{ gap: 4 }}>
          <ChevronLeft size={16} aria-hidden /> {t('Tenants')}
        </Link>
        <PageHeader title={detail.name} description={`${detail.branding.productName} · ${detail.hostname}`}>
          <span data-testid="tenant-status">
            {detail.status === 'ACTIVE' ? (
              <Pill kind="success" dot>
                {t('Active')}
              </Pill>
            ) : (
              <Pill kind="warning" dot>
                {t('Suspended')}
              </Pill>
            )}
          </span>
          <a className="button secondary" href={`https://${detail.hostname}`} target="_blank" rel="noreferrer">
            <ExternalLink size={15} aria-hidden /> {t('Open site')}
          </a>
          {detail.status === 'ACTIVE' ? (
            <button
              type="button"
              className="secondary"
              onClick={() => confirm(t('Suspend {name}? Its people are signed out and can’t call.', { name: detail.name })) && void update(api.platform.suspendTenant(id))}
            >
              <Ban size={15} aria-hidden /> {t('Suspend')}
            </button>
          ) : (
            <button type="button" onClick={() => void update(api.platform.activateTenant(id))}>
              <Play size={15} aria-hidden /> {t('Reactivate')}
            </button>
          )}
        </PageHeader>
      </div>

      <div className="info-tiles">
        <div className="card tile">
          <span className="tile-label">{t('People')}</span>
          <span className="tile-value tabular">{detail.userCount}</span>
        </div>
        <div className="card tile">
          <span className="tile-label">{t('Administrators')}</span>
          <span className="tile-value tabular">{detail.adminCount}</span>
        </div>
        <div className="card tile">
          <span className="tile-label">{t('Simultaneous calls')}</span>
          <span className="tile-value tabular">{detail.maxConcurrentCalls}</span>
        </div>
        <div className="card tile">
          <span className="tile-label">{t('Daily minutes cap')}</span>
          <span className="tile-value tabular">{detail.dailyMinutesCap.toLocaleString(locale)}</span>
        </div>
        <div className="card tile">
          <span className="tile-label">{t('AI tokens per month')}</span>
          <span className="tile-value tabular">{detail.aiMonthlyTokenBudget.toLocaleString(locale)}</span>
        </div>
      </div>

      <section className="card">
        <div className="card-header">
          <div>
            <h2>{t('Administrators')}</h2>
            <p className="subtle">{t('They manage their own people, regions and branding.')}</p>
          </div>
          <button type="button" onClick={() => setAddingAdmin(true)} data-testid="add-tenant-admin">
            <UserPlus size={16} aria-hidden /> {t('Add administrator')}
          </button>
        </div>
        <div className="card-body muted">
          {detail.adminCount === 0
            ? t('No administrator yet. Add one so the company can set itself up.')
            : tp(
                detail.adminCount,
                '{n} active administrator. For privacy, the console doesn’t list a tenant’s people.',
                '{n} active administrators. For privacy, the console doesn’t list a tenant’s people.',
              )}
        </div>
      </section>

      <section className="stack">
        <h2>{t('Settings')}</h2>
        <SettingsForm tenant={detail} onSaved={(saved) => void update(Promise.resolve(saved))} />
      </section>

      <section className="stack">
        <h2>{t('Branding')}</h2>
        <BrandingForm
          key={`${detail.branding.logoVersion}-${detail.branding.faviconVersion}`}
          branding={detail.branding}
          imageUrl={(kind, version) => `https://${detail.hostname}/api/branding/${kind}?v=${version}`}
          save={(branding) => update(api.platform.updateTenant(id, { branding }))}
          uploadImage={(kind, file) => update(api.platform.uploadTenantImage(id, kind, file))}
        />
      </section>

      <section className="stack">
        <h2>{t('Usage')}</h2>
        <div className="card table-wrap">
          {usage.data && usage.data.length === 0 ? (
            <EmptyState icon={Activity} title={t('No usage yet')}>
              <p>{t('Call minutes, recordings, transcription and AI use appear here by month.')}</p>
            </EmptyState>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t('Month')}</th>
                  <th>{t('Calls')}</th>
                  <th>{t('Recordings')}</th>
                  <th>{t('Transcription')}</th>
                  <th>{t('AI tokens')}</th>
                </tr>
              </thead>
              <tbody>
                {usage.data?.map((month) => (
                  <tr key={month.month}>
                    <td>{month.month}</td>
                    <td className="tabular">{formatMinutes(month.callSeconds)}</td>
                    <td className="tabular">{formatBytes(month.recordingBytes)}</td>
                    <td className="tabular">{formatMinutes(month.transcriptionSeconds)}</td>
                    <td className="tabular">{Number(month.aiTokens).toLocaleString(locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="stack">
        <h2>{t('Platform staff activity')}</h2>
        <div className="card table-wrap">
          {audit.data && audit.data.length === 0 ? (
            <EmptyState icon={ShieldCheck} title={t('Nothing yet')} />
          ) : (
            <table>
              <tbody>
                {audit.data?.map((entry) => (
                  <tr key={entry.id}>
                    <td>{actionNames[entry.action] ?? entry.action}</td>
                    <td className="muted" style={{ textAlign: 'right' }}>
                      {new Date(entry.createdAt).toLocaleString(locale)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <Dialog
        open={addingAdmin}
        onClose={() => setAddingAdmin(false)}
        title={t('Add administrator')}
        description={t('They set a password, then set up an authenticator app at first sign-in.')}
      >
        {addingAdmin && (
          <AddAdmin
            tenantId={id}
            onDone={() => {
              setAddingAdmin(false);
              tenant.reload();
              audit.reload();
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

function SettingsForm({ tenant, onSaved }: { tenant: TenantDetail; onSaved: (tenant: TenantDetail) => void }) {
  const [name, setName] = useState(tenant.name);
  const [hostname, setHostname] = useState(tenant.hostname);
  const [timeZone, setTimeZone] = useState(tenant.timeZone);
  const [maxConcurrentCalls, setMaxConcurrentCalls] = useState(String(tenant.maxConcurrentCalls));
  const [dailyMinutesCap, setDailyMinutesCap] = useState(String(tenant.dailyMinutesCap));
  const [aiMonthlyTokenBudget, setAiBudget] = useState(String(tenant.aiMonthlyTokenBudget));
  const [saved, setSaved] = useState(false);
  const form = useSubmit();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    const input = {
      name,
      hostname,
      timeZone,
      maxConcurrentCalls: Number(maxConcurrentCalls),
      dailyMinutesCap: Number(dailyMinutesCap),
      aiMonthlyTokenBudget: Number(aiMonthlyTokenBudget),
    };
    const result = await form.submit(updateTenantSchema, input, (value) => api.platform.updateTenant(tenant.id, value));
    if (result) {
      setSaved(true);
      onSaved(result);
    }
  }

  return (
    <form className="card" onSubmit={submit} noValidate>
      <div className="card-body stack">
        <div className="form-grid">
          <Field label={t('Company')} error={form.fields.name}>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('Hostname')} error={form.fields.hostname}>
            <input value={hostname} onChange={(e) => setHostname(e.target.value)} />
          </Field>
          <Field label={t('Time zone')} error={form.fields.timeZone}>
            <TimeZoneSelect value={timeZone} onChange={setTimeZone} />
          </Field>
          <Field label={t('Simultaneous calls')} error={form.fields.maxConcurrentCalls}>
            <input inputMode="numeric" value={maxConcurrentCalls} onChange={(e) => setMaxConcurrentCalls(e.target.value)} />
          </Field>
          <Field label={t('Daily minutes cap')} error={form.fields.dailyMinutesCap}>
            <input inputMode="numeric" value={dailyMinutesCap} onChange={(e) => setDailyMinutesCap(e.target.value)} />
          </Field>
          <Field label={t('AI tokens per month')} hint={t('Call reviews pause when they are used up')} error={form.fields.aiMonthlyTokenBudget}>
            <input inputMode="numeric" value={aiMonthlyTokenBudget} onChange={(e) => setAiBudget(e.target.value)} />
          </Field>
        </div>
        {form.error && <Notice kind="error">{form.error}</Notice>}
        {saved && <Notice kind="success">{t('Saved.')}</Notice>}
      </div>
      <div className="card-footer">
        <button type="submit" disabled={form.busy}>
          {t('Save settings')}
        </button>
      </div>
    </form>
  );
}

function AddAdmin({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [extension, setExtension] = useState('100');
  const [link, setLink] = useState<PasswordLink>();
  const form = useSubmit();

  async function submit(event: FormEvent) {
    event.preventDefault();
    const created = await form.submit(createTenantAdminSchema, { name, email, extension }, (value) => api.platform.createTenantAdmin(tenantId, value));
    if (created) setLink(created);
  }

  if (link) {
    return (
      <>
        <div className="dialog-body">
          {link.email === 'off' && <Notice kind="success">{t('Send {name} this invite link.', { name })}</Notice>}
          <CopyLink {...link} to={email} />
        </div>
        <div className="dialog-footer">
          <button type="button" onClick={onDone}>
            {t('Done')}
          </button>
        </div>
      </>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <div className="dialog-body">
        <div className="form-grid">
          <Field label={t('Name')} error={form.fields.name}>
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="admin-name" />
          </Field>
          <Field label={t('Email')} error={form.fields.email}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" />
          </Field>
          <Field label={t('Extension')} error={form.fields.extension}>
            <input inputMode="numeric" value={extension} onChange={(e) => setExtension(e.target.value)} />
          </Field>
        </div>
        {form.error && <Notice kind="error">{form.error}</Notice>}
      </div>
      <div className="dialog-footer">
        <button type="button" className="secondary" onClick={onDone}>
          {t('Cancel')}
        </button>
        <button type="submit" disabled={form.busy} data-testid="save-admin">
          {t('Create invite link')}
        </button>
      </div>
    </form>
  );
}
